/**
 * ScrollToTop — composant utilitaire qui remonte la fenêtre en haut à chaque
 * changement de route (pathname). À monter une seule fois à l'intérieur du
 * <BrowserRouter> dans App.js, juste avant les <Routes>.
 *
 * - Hash anchors (e.g. `/#news`) : on attend que l'élément cible soit
 *   monté (lazy data sections), puis on scrolle vers lui en `smooth`.
 *   Retry max 10× toutes les 150 ms (cap 1.5 s) avant abandon silencieux.
 * - Respecte `prefers-reduced-motion` : scroll instantané pour ces
 *   utilisateurs, sinon scroll "auto" car les utilisateurs attendent un
 *   saut en haut, pas une animation longue.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      // Hash anchor: try to scroll to the target. The target may not be
      // mounted yet (lazy data fetch), so retry a few times.
      const id = hash.replace(/^#/, '');
      if (!id) return;
      let attempts = 0;
      const tick = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        attempts += 1;
        if (attempts < 10) setTimeout(tick, 150);
      };
      // First attempt on next paint to give React time to mount the route.
      const initial = setTimeout(tick, 50);
      return () => clearTimeout(initial);
    }
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } catch {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  return null;
};

export default ScrollToTop;
